import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Alert, AlertTitle } from '@mui/material'

const PTSBookings = () => {
  const [loading, setLoading] = useState(true)
  const [defaultBookingMode, setDefaultBookingMode] = useState('Ask Every Time')
  const [enforceBookingMode, setEnforceBookingMode] = useState(false)
  const [capacityEnforcement, setCapacityEnforcement] = useState('warn')
  const [smsToPatient, setSmsToPatient] = useState(true)
  const [emailToWard, setEmailToWard] = useState(true)
  const [eligibilityScreeningEnabled, setEligibilityScreeningEnabled] =
    useState(false)
  const [eligibilityQuestions, setEligibilityQuestions] = useState([])
  const [minimumDataset, setMinimumDataset] = useState('standard')
  const [minimumDatasetPortal, setMinimumDatasetPortal] = useState(false)

  const { setToast } = useContext(GlobalContext)
  useEffect(() => {
    API.get(
      '/settings?select=patientTransportSettings.booking%20patientTransportSettings.planning%20patientTransportSettings.autoAssigned',
    ).then((res) => {
      if (res.status === 200) {
        const data = res.data.patientTransportSettings
        setDefaultBookingMode(
          data?.booking?.defaultBookingMode ??
            data?.defaultBookingMode ??
            'Ask Every Time',
        )
        ;(data?.booking?.enforceBookingMode ?? data?.enforceBookingMode) &&
          setEnforceBookingMode(true)
        setCapacityEnforcement(data?.planning?.capacityEnforcement ?? 'warn')
        setSmsToPatient(data?.autoAssigned?.notifications?.smsToPatient ?? true)
        setEmailToWard(data?.autoAssigned?.notifications?.emailToWard ?? true)
        setEligibilityScreeningEnabled(
          !!data?.booking?.eligibilityScreeningEnabled,
        )
        setEligibilityQuestions(data?.booking?.eligibilityQuestions || [])
        setMinimumDataset(data?.booking?.minimumDataset || 'standard')
        setMinimumDatasetPortal(!!data?.booking?.minimumDatasetPortal)
        setLoading(false)
      }
    })
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    API.put('/site-config', {
      'patientTransportSettings.booking.defaultBookingMode': defaultBookingMode,
      'patientTransportSettings.booking.enforceBookingMode': enforceBookingMode,
      'patientTransportSettings.planning.capacityEnforcement':
        capacityEnforcement,
      'patientTransportSettings.autoAssigned.notifications.smsToPatient':
        smsToPatient,
      'patientTransportSettings.autoAssigned.notifications.emailToWard':
        emailToWard,
      'patientTransportSettings.booking.eligibilityScreeningEnabled':
        eligibilityScreeningEnabled,
      'patientTransportSettings.booking.eligibilityQuestions':
        eligibilityQuestions.filter((question) => question.trim()),
      'patientTransportSettings.booking.minimumDataset': minimumDataset,
      'patientTransportSettings.booking.minimumDatasetPortal':
        minimumDataset === 'reduced' ? minimumDatasetPortal : false,
    }).then((res) => {
      if (res.status === 200) {
        setToast(true)
      }
    })
  }

  return (
    <Box padding={1}>
      <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
        <Grid item xs={12} order={1}>
          <Typography variant="title2">PTS Bookings</Typography>
        </Grid>
        {loading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
        <Grid item xs={12} lg={9} order={2}>
          <FormControl fullWidth>
            <InputLabel>Default Booking Mode</InputLabel>
            <Select
              label="Default Booking Mode"
              value={defaultBookingMode}
              onChange={(e) => setDefaultBookingMode(e.target.value)}
            >
              {['One-Off Transfer', 'Part of Shift/Duty', 'Ask Every Time'].map(
                (x) => (
                  <MenuItem
                    key={x}
                    value={x}
                    disabled={x === 'Ask Every Time' && enforceBookingMode}
                  >
                    {x}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={9} order={{ xs: 3, lg: 4 }}>
          <Alert severity="info">
            <AlertTitle>
              This sets the default view for all users when creating a new
              booking.
            </AlertTitle>
            One-Off Transfers are ideal for ad-hoc or private jobs and can be
            scheduled later
            <br />
            Shift/duty bookings are better for regular work where jobs are
            planned around crews and vehicles
          </Alert>
        </Grid>
        <Grid item xs={12} lg={3} order={{ xs: 4, lg: 3 }}>
          <Box display="flex" alignItems="center" height="100%">
            <FormControlLabel
              label="Enforce Booking Mode"
              value={enforceBookingMode}
              control={
                <Switch
                  checked={enforceBookingMode}
                  onChange={(e) => {
                    setEnforceBookingMode(e.target.checked)
                    if (
                      e.target.checked &&
                      defaultBookingMode === 'Ask Every Time'
                    ) {
                      console.log('should change')
                      setDefaultBookingMode('One-Off Transfer')
                    }
                  }}
                />
              }
            />
          </Box>
        </Grid>
        <Grid item xs={12} lg={3} order={4}>
          <Alert severity="info">
            <AlertTitle>Prevent Users from selecting</AlertTitle>
            When enabled, all new bookings will use your default booking mode
          </Alert>
        </Grid>
        <Grid item xs={12} lg={9} order={5}>
          <FormControl fullWidth>
            <InputLabel>Vehicle Capacity Enforcement</InputLabel>
            <Select
              label="Vehicle Capacity Enforcement"
              value={capacityEnforcement}
              onChange={(e) => setCapacityEnforcement(e.target.value)}
            >
              <MenuItem value="warn">Warn — allow but flag the planner</MenuItem>
              <MenuItem value="block">Block — reject the assignment</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={3} order={6}>
          <Alert severity="info">
            <AlertTitle>Journey planning</AlertTitle>
            Applies when a journey is assigned to a duty whose vehicle can't
            carry it (e.g. a stretcher patient onto a car).
          </Alert>
        </Grid>

        <Grid item xs={12} order={7}>
          <Typography variant="title2" sx={{ mt: 1 }}>
            Live tracking notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sent once, the first time a journey's crew goes Mobile, with a
            link to a public tracking page (status + a coarse ETA — no login
            required).
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} order={8}>
          <FormControlLabel
            label="Text the patient"
            control={
              <Switch
                checked={smsToPatient}
                onChange={(e) => setSmsToPatient(e.target.checked)}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} order={9}>
          <FormControlLabel
            label="Email the ward"
            control={
              <Switch
                checked={emailToWard}
                onChange={(e) => setEmailToWard(e.target.checked)}
              />
            }
          />
        </Grid>

        <Grid item xs={12} order={10}>
          <Typography variant="title2" sx={{ mt: 1 }}>
            Minimum booking dataset
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How much patient information is needed before a booking can be
            created.
          </Typography>
        </Grid>
        <Grid item xs={12} lg={9} order={10}>
          <FormControl fullWidth>
            <InputLabel>Minimum booking dataset</InputLabel>
            <Select
              label="Minimum booking dataset"
              value={minimumDataset}
              onChange={(e) => setMinimumDataset(e.target.value)}
            >
              <MenuItem value="standard">
                Standard — full patient record required
              </MenuItem>
              <MenuItem value="reduced">
                Reduced — name only; remaining details flagged as outstanding
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={3} order={10}>
          <Alert severity="info">
            <AlertTitle>Create now, complete later</AlertTitle>
            With the reduced dataset, bookings can be created from partial
            information (e.g. a transfer confirmation email). Missing details
            are flagged on the booking and must be completed before a journey
            can be closed.
          </Alert>
        </Grid>
        {minimumDataset === 'reduced' && (
          <Grid item xs={12} order={10}>
            <FormControlLabel
              label="Also apply the reduced dataset to hospital portal users"
              control={
                <Switch
                  checked={minimumDatasetPortal}
                  onChange={(e) => setMinimumDatasetPortal(e.target.checked)}
                />
              }
            />
          </Grid>
        )}

        <Grid item xs={12} order={10}>
          <Typography variant="title2" sx={{ mt: 1 }}>
            Eligibility screening
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Optional questionnaire shown to hospital portal users when they
            create a booking. The outcome is stored on the booking for
            funding audit — it does not block the booking.
          </Typography>
        </Grid>
        <Grid item xs={12} order={11}>
          <FormControlLabel
            label="Enable eligibility screening on portal bookings"
            control={
              <Switch
                checked={eligibilityScreeningEnabled}
                onChange={(e) =>
                  setEligibilityScreeningEnabled(e.target.checked)
                }
              />
            }
          />
        </Grid>
        {eligibilityScreeningEnabled && (
          <Grid item xs={12} order={12}>
            <Stack spacing={1}>
              {eligibilityQuestions.map((question, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    value={question}
                    onChange={(e) =>
                      setEligibilityQuestions((prev) =>
                        prev.map((q, i) => (i === index ? e.target.value : q)),
                      )
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setEligibilityQuestions((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ alignSelf: 'flex-start' }}
                onClick={() =>
                  setEligibilityQuestions((prev) => [...prev, ''])
                }
              >
                Add question
              </Button>
            </Stack>
          </Grid>
        )}

        <Grid item xs={12} order={13}>
          <Button variant="contained" fullWidth color="anGreen" type="submit">
            Submit
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PTSBookings
